<template>
  <div class="role-claims-admin p-4">
    <admin-nav />
    
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <Shield :size="28" class="me-2" />
        Roles & Claims Administration
      </h2>
    </div>

    <div class="row">
      <!-- Roles Panel -->
      <div class="col-lg-4 mb-4">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <Key :size="20" class="me-2" />
              Roles
            </h5>
            <button class="btn btn-sm btn-primary" @click="showRoleModal = true">
              <Plus :size="16" class="me-1" />
              Add Role
            </button>
          </div>
          <div class="card-body p-0">
            <div class="list-group list-group-flush">
              <div 
                v-for="role in roles" 
                :key="role.Id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                :class="{ active: selectedRole?.Id === role.Id }"
                @click="selectRole(role)"
              >
                <div>
                  <strong>{{ role.Name }}</strong>
                  <span v-if="role.IsDefault" class="badge bg-info ms-2">Default</span>
                </div>
                <div class="btn-group btn-group-sm" @click.stop>
                  <button class="btn btn-outline-secondary" @click="editRole(role)" title="Edit">
                    <Edit :size="14" />
                  </button>
                  <button class="btn btn-outline-danger" @click="deleteRole(role)" title="Delete">
                    <Trash2 :size="14" />
                  </button>
                </div>
              </div>
              <div v-if="roles.length === 0" class="list-group-item text-muted text-center">
                No roles defined
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Role Claims Panel -->
      <div class="col-lg-8 mb-4">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <FileCheck :size="20" class="me-2" />
              {{ selectedRole ? `Claims for "${selectedRole.Name}"` : 'Role Claims' }}
            </h5>
            <button 
              class="btn btn-sm btn-primary" 
              @click="showClaimModal = true"
              :disabled="!selectedRole"
            >
              <Plus :size="16" class="me-1" />
              Add Claim
            </button>
          </div>
          <div class="card-body">
            <div v-if="!selectedRole" class="text-muted text-center py-4">
              <Shield :size="48" class="mb-3 opacity-50" />
              <p>Select a role to view and manage its claims</p>
            </div>
            <div v-else>
              <table class="table table-sm table-hover" v-if="roleClaims.length > 0">
                <thead>
                  <tr>
                    <th>Claim Type</th>
                    <th>Claim Value</th>
                    <th width="80">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="claim in roleClaims" :key="claim.Id">
                    <td><code>{{ claim.ClaimType }}</code></td>
                    <td>{{ claim.ClaimValue }}</td>
                    <td>
                      <button class="btn btn-sm btn-outline-danger" @click="deleteRoleClaim(claim)">
                        <Trash2 :size="14" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="text-muted text-center py-3">
                No claims assigned to this role
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Technicians Panel -->
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">
          <Users :size="20" class="me-2" />
          Technician Role & Claim Assignments
        </h5>
      </div>
      <div class="card-body">
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Technician</th>
                <th>Email</th>
                <th>Assigned Roles</th>
                <th>Direct Claims</th>
                <th width="150">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tech in technicians" :key="tech.Id">
                <td>
                  <strong>{{ tech.TechnicianName }}</strong>
                </td>
                <td>
                  <small class="text-muted">{{ tech.Email || 'N/A' }}</small>
                </td>
                <td>
                  <span 
                    v-for="role in tech.Roles" 
                    :key="role.RoleId" 
                    class="badge bg-primary me-1"
                  >
                    {{ role.RoleName }}
                  </span>
                  <span v-if="tech.Roles.length === 0" class="text-muted">None</span>
                </td>
                <td>
                  <span 
                    v-for="claim in tech.Claims" 
                    :key="claim.Id" 
                    class="badge bg-secondary me-1"
                    :title="`${claim.ClaimType}: ${claim.ClaimValue}`"
                  >
                    {{ claim.ClaimType }}
                  </span>
                  <span v-if="tech.Claims.length === 0" class="text-muted">None</span>
                </td>
                <td>
                  <div class="btn-group btn-group-sm">
                    <button class="btn btn-outline-primary" @click="editTechRoles(tech)" title="Manage Roles">
                      <Key :size="14" />
                    </button>
                    <button class="btn btn-outline-secondary" @click="editTechClaims(tech)" title="Manage Claims">
                      <FileCheck :size="14" />
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="technicians.length === 0">
                <td colspan="5" class="text-center text-muted">No technicians found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Role Modal -->
    <div class="modal" :class="{ 'show d-block': showRoleModal }" v-if="showRoleModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingRole ? 'Edit Role' : 'Create Role' }}</h5>
            <button type="button" class="btn-close" @click="closeRoleModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Role Name *</label>
              <input type="text" class="form-control" v-model="roleForm.name" required>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="checkbox" v-model="roleForm.isDefault" id="isDefault">
              <label class="form-check-label" for="isDefault">Default Role</label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeRoleModal">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveRole" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              {{ editingRole ? 'Update' : 'Create' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showRoleModal"></div>

    <!-- Claim Modal -->
    <div class="modal" :class="{ 'show d-block': showClaimModal }" v-if="showClaimModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Add Claim to "{{ selectedRole?.Name }}"</h5>
            <button type="button" class="btn-close" @click="showClaimModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Claim Type *</label>
              <select class="form-select" v-model="claimForm.claimType">
                <option value="">Select a claim type...</option>
                <option v-for="claim in availableClaims" :key="claim.Type" :value="claim.Type">
                  {{ claim.Type }} - {{ claim.Description }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Claim Value *</label>
              <input type="text" class="form-control" v-model="claimForm.claimValue" placeholder="e.g., read, write, admin">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showClaimModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="addRoleClaim" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Add Claim
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showClaimModal"></div>

    <!-- Tech Roles Modal -->
    <div class="modal" :class="{ 'show d-block': showTechRolesModal }" v-if="showTechRolesModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Manage Roles for "{{ selectedTech?.TechnicianName }}"</h5>
            <button type="button" class="btn-close" @click="showTechRolesModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="form-check mb-2" v-for="role in roles" :key="role.Id">
              <input 
                class="form-check-input" 
                type="checkbox" 
                :id="'role-' + role.Id"
                :value="role.Id"
                v-model="selectedTechRoles"
              >
              <label class="form-check-label" :for="'role-' + role.Id">
                {{ role.Name }}
                <span v-if="role.IsDefault" class="badge bg-info ms-1">Default</span>
              </label>
            </div>
            <div v-if="roles.length === 0" class="text-muted">No roles available</div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showTechRolesModal = false">Cancel</button>
            <button type="button" class="btn btn-primary" @click="saveTechRoles" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Save Roles
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showTechRolesModal"></div>

    <!-- Tech Claims Modal -->
    <div class="modal" :class="{ 'show d-block': showTechClaimsModal }" v-if="showTechClaimsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Manage Claims for "{{ selectedTech?.TechnicianName }}"</h5>
            <button type="button" class="btn-close" @click="showTechClaimsModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-4">
              <h6>Current Claims</h6>
              <table class="table table-sm" v-if="selectedTech?.Claims?.length > 0">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Value</th>
                    <th width="60"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="claim in selectedTech.Claims" :key="claim.Id">
                    <td><code>{{ claim.ClaimType }}</code></td>
                    <td>{{ claim.ClaimValue }}</td>
                    <td>
                      <button class="btn btn-sm btn-outline-danger" @click="removeTechClaim(claim)">
                        <Trash2 :size="14" />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p v-else class="text-muted">No direct claims assigned</p>
            </div>
            <hr>
            <h6>Add New Claim</h6>
            <div class="row g-2">
              <div class="col-md-4">
                <select class="form-select form-select-sm" v-model="techClaimForm.claimType">
                  <option value="">Claim Type...</option>
                  <option v-for="claim in availableClaims" :key="claim.Type" :value="claim.Type">
                    {{ claim.Type }}
                  </option>
                </select>
              </div>
              <div class="col-md-5">
                <input type="text" class="form-control form-control-sm" v-model="techClaimForm.claimValue" placeholder="Claim Value">
              </div>
              <div class="col-md-3">
                <button class="btn btn-sm btn-primary w-100" @click="addTechClaim" :disabled="saving">
                  <Plus :size="14" class="me-1" />
                  Add
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showTechClaimsModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showTechClaimsModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import ApiService from '@/services/ApiService';
import AdminNav from '@/components/Shared/AdminNav.vue';
import { useAppStore } from '@/stores/AppStore';
import { 
  Shield, 
  Key, 
  Plus, 
  Edit, 
  Trash2, 
  FileCheck, 
  Users 
} from 'lucide-vue-next';

interface Role {
  Id: string;
  Name: string;
  NName?: string;
  IsDefault?: boolean;
}

interface RoleClaim {
  Id: string;
  RoleId: string;
  ClaimType: string;
  ClaimValue: string;
}

interface TechClaim {
  Id: string;
  ClaimType: string;
  ClaimValue: string;
}

interface TechRole {
  RoleId: string;
  RoleName: string;
}

interface Technician {
  Id: number;
  TechnicianName: string;
  Email?: string;
  Active?: boolean;
  Roles: TechRole[];
  Claims: TechClaim[];
}

interface AvailableClaim {
  Type: string;
  Description: string;
}

const appStore = useAppStore();

const roles = ref<Role[]>([]);
const roleClaims = ref<RoleClaim[]>([]);
const technicians = ref<Technician[]>([]);
const availableClaims = ref<AvailableClaim[]>([]);
const selectedRole = ref<Role | null>(null);
const selectedTech = ref<Technician | null>(null);
const selectedTechRoles = ref<string[]>([]);

// Helper to check if the updated technician is the current logged-in user
const isCurrentUser = (techId: number): boolean => {
  return appStore.account.user?.UserId === techId;
};

const showRoleModal = ref(false);
const showClaimModal = ref(false);
const showTechRolesModal = ref(false);
const showTechClaimsModal = ref(false);
const editingRole = ref<Role | null>(null);
const saving = ref(false);

const roleForm = ref({
  name: '',
  isDefault: false
});

const claimForm = ref({
  claimType: '',
  claimValue: ''
});

const techClaimForm = ref({
  claimType: '',
  claimValue: ''
});

const loadRoles = async () => {
  try {
    roles.value = await ApiService.loadRoles(true);
  } catch (error: any) {
    console.error('Error loading roles:', error);
  }
};

const loadTechnicians = async () => {
  try {
    technicians.value = await ApiService.loadTechniciansWithRoles(true);
  } catch (error: any) {
    console.error('Error loading technicians:', error);
  }
};

const loadAvailableClaims = async () => {
  try {
    availableClaims.value = await ApiService.loadAvailableClaims(true);
  } catch (error: any) {
    console.error('Error loading available claims:', error);
  }
};

const selectRole = async (role: Role) => {
  selectedRole.value = role;
  try {
    roleClaims.value = await ApiService.loadRoleClaims(role.Id, true);
  } catch (error: any) {
    console.error('Error loading role claims:', error);
    roleClaims.value = [];
  }
};

const editRole = (role: Role) => {
  editingRole.value = role;
  roleForm.value = {
    name: role.Name || '',
    isDefault: role.IsDefault || false
  };
  showRoleModal.value = true;
};

const closeRoleModal = () => {
  showRoleModal.value = false;
  editingRole.value = null;
  roleForm.value = { name: '', isDefault: false };
};

const saveRole = async () => {
  if (!roleForm.value.name) {
    alert('Role name is required');
    return;
  }

  saving.value = true;
  try {
    const roleData = {
      Id: editingRole.value?.Id || null,
      Name: roleForm.value.name,
      IsDefault: roleForm.value.isDefault
    };
    await ApiService.saveRole(roleData);
    closeRoleModal();
    await loadRoles();
  } catch (error: any) {
    console.error('Error saving role:', error);
  } finally {
    saving.value = false;
  }
};

const deleteRole = async (role: Role) => {
  if (!confirm(`Are you sure you want to delete the role "${role.Name}"? This will also remove all associated claims and user assignments.`)) {
    return;
  }

  try {
    await ApiService.deleteRole(role.Id);
    if (selectedRole.value?.Id === role.Id) {
      selectedRole.value = null;
      roleClaims.value = [];
    }
    await loadRoles();
    await loadTechnicians();
  } catch (error: any) {
    console.error('Error deleting role:', error);
  }
};

const addRoleClaim = async () => {
  if (!claimForm.value.claimType || !claimForm.value.claimValue) {
    alert('Both claim type and value are required');
    return;
  }

  saving.value = true;
  try {
    const claimData = {
      ClaimType: claimForm.value.claimType,
      ClaimValue: claimForm.value.claimValue
    };
    await ApiService.addClaimToRole(selectedRole.value!.Id, claimData);
    showClaimModal.value = false;
    claimForm.value = { claimType: '', claimValue: '' };
    await selectRole(selectedRole.value!);
  } catch (error: any) {
    console.error('Error adding claim:', error);
  } finally {
    saving.value = false;
  }
};

const deleteRoleClaim = async (claim: RoleClaim) => {
  if (!confirm('Are you sure you want to remove this claim?')) {
    return;
  }

  try {
    await ApiService.removeClaimFromRole(claim.Id);
    await selectRole(selectedRole.value!);
  } catch (error: any) {
    console.error('Error deleting claim:', error);
  }
};

const editTechRoles = (tech: Technician) => {
  selectedTech.value = tech;
  selectedTechRoles.value = tech.Roles.map(r => r.RoleId);
  showTechRolesModal.value = true;
};

const saveTechRoles = async () => {
  if (!selectedTech.value) return;
  
  const techId = selectedTech.value.Id;
  saving.value = true;
  try {
    await ApiService.updateTechnicianRoles(
      techId.toString(), 
      selectedTechRoles.value
    );
    showTechRolesModal.value = false;
    await loadTechnicians();
    
    // If we updated the current user's roles, refresh their session to get updated JWT
    if (isCurrentUser(techId)) {
      console.debug('Current user roles updated, refreshing session...');
      await appStore.refreshSession();
    }
  } catch (error: any) {
    console.error('Error saving roles:', error);
    alert('Error saving roles: ' + (error.message || 'Unknown error'));
  } finally {
    saving.value = false;
  }
};

const editTechClaims = (tech: Technician) => {
  selectedTech.value = tech;
  techClaimForm.value = { claimType: '', claimValue: '' };
  showTechClaimsModal.value = true;
};

const addTechClaim = async () => {
  if (!techClaimForm.value.claimType || !techClaimForm.value.claimValue) {
    alert('Both claim type and value are required');
    return;
  }

  const techId = selectedTech.value!.Id;
  saving.value = true;
  try {
    const claimData = {
      ClaimType: techClaimForm.value.claimType,
      ClaimValue: techClaimForm.value.claimValue
    };
    await ApiService.addClaimToTechnician(techId.toString(), claimData);
    techClaimForm.value = { claimType: '', claimValue: '' };
    await loadTechnicians();
    // Update the selected tech with new data
    selectedTech.value = technicians.value.find(t => t.Id === techId) || null;
    
    // If we updated the current user's claims, refresh their session to get updated JWT
    if (isCurrentUser(techId)) {
      console.debug('Current user claims updated, refreshing session...');
      await appStore.refreshSession();
    }
  } catch (error: any) {
    console.error('Error adding claim:', error);
  } finally {
    saving.value = false;
  }
};

const removeTechClaim = async (claim: TechClaim) => {
  if (!confirm('Are you sure you want to remove this claim?')) {
    return;
  }

  const techId = selectedTech.value!.Id;
  try {
    await ApiService.removeClaimFromTechnician(techId.toString(), claim.Id);
    await loadTechnicians();
    selectedTech.value = technicians.value.find(t => t.Id === techId) || null;
    
    // If we updated the current user's claims, refresh their session to get updated JWT
    if (isCurrentUser(techId)) {
      console.debug('Current user claims updated, refreshing session...');
      await appStore.refreshSession();
    }
  } catch (error: any) {
    console.error('Error removing claim:', error);
  }
};

onMounted(() => {
  loadRoles();
  loadTechnicians();
  loadAvailableClaims();
});
</script>

<style scoped>
.role-claims-admin {
  max-width: 1400px;
  margin: 0 auto;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.list-group-item.active {
  background-color: #132341;
  border-color: #132341;
}

.list-group-item-action {
  cursor: pointer;
}

.card-header {
  background-color: #f8f9fa;
}
</style>
